import { useEffect, useState } from "react";
import { Autocomplete, Grid, MenuItem, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { useIntegrationDataService } from "./integrationDataService";
import { useTenantService } from "../tenants/tenantService";
import { FormContainer, FormHeader, FormSection } from "../shared/FormLayout";

const SYSTEMS = ["FAMAC", "PROPELY", "PID", "FDVWEB", "FM_MODBUS"] as const;
type SystemType = typeof SYSTEMS[number];

const SYSTEM_FIELDS: Record<SystemType, { key: string; label: string; secret: boolean }[]> = {
  FAMAC: [
    { key: "token", label: "Token", secret: true },
  ],
  PROPELY: [
    { key: "companyId", label: "Company ID", secret: false },
    { key: "clientId", label: "Client ID", secret: false },
    { key: "secret", label: "Secret", secret: true },
  ],
  PID: [
    { key: "clientId", label: "Client ID", secret: false },
    { key: "secret", label: "Secret", secret: true },
  ],
  FDVWEB: [
    { key: "token", label: "Token", secret: true },
  ],
  FM_MODBUS: [
    { key: "token", label: "Token", secret: true },
  ],
};

const validationSchema = Yup.object().shape({
  tenantId: Yup.string().required("Tenant er påkrevd"),
  system: Yup.string().required("System er påkrevd"),
});

export const CreateIntegrationForm: React.FC = () => {
  const integrationService = useIntegrationDataService();
  const tenantService = useTenantService();
  const history = useHistory();

  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<SystemType | "">("");
  const [credentialValues, setCredentialValues] = useState<Record<string, string>>({});

  useEffect(() => {
    tenantService.getAll().then(setTenants);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { register, handleSubmit, setValue, formState } = useForm({
    defaultValues: { tenantId: "", system: "" },
    resolver: yupResolver(validationSchema),
  });
  const { errors, isSubmitting } = formState;

  const handleSystemChange = (system: string) => {
    setSelectedSystem(system as SystemType);
    setValue("system", system);
    setCredentialValues({});
  };

  const onSubmit = async (values: any) => {
    const data: Record<string, string> = {};
    if (selectedSystem) {
      for (const field of SYSTEM_FIELDS[selectedSystem]) {
        data[field.key] = credentialValues[field.key] ?? "";
      }
    }
    await integrationService.post({
      tenantId: values.tenantId,
      system: values.system,
      data,
      createdOn: new Date().toUTCString(),
    });
    history.push("/integrations");
  };

  const systemFields = selectedSystem ? SYSTEM_FIELDS[selectedSystem] : [];

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <FormHeader section="Integrasjoner" title="Ny integrasjon" isSubmitting={isSubmitting} />

      <FormSection title="Integrasjon">
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={tenants}
            getOptionLabel={(t: any) => t.name ?? ""}
            onChange={(_, t: any) => setValue("tenantId", t?.id ?? "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tenant"
                size="small"
                fullWidth
                error={!!errors.tenantId}
                helperText={errors.tenantId?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            {...register("system")}
            select
            label="System"
            size="small"
            fullWidth
            defaultValue=""
            onChange={(e) => handleSystemChange(e.target.value)}
            error={!!errors.system}
            helperText={errors.system?.message}
          >
            {SYSTEMS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </FormSection>

      {!!systemFields.length && (
        <FormSection title="Pålogging">
          {systemFields.map((field) => (
            <Grid item xs={12} md={6} key={field.key}>
              <TextField
                label={field.label}
                type={field.secret ? "password" : "text"}
                size="small"
                fullWidth
                value={credentialValues[field.key] ?? ""}
                onChange={(e) => setCredentialValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
              />
            </Grid>
          ))}
        </FormSection>
      )}
    </FormContainer>
  );
};
